import { Button, DatePicker, Icon } from 'antd'
import moment from 'moment'
import React from 'react'
import styled from 'styled-components/macro'
import {
  buildFoodSafetyBrowseWindow,
  canBrowseFoodSafetyNext,
  canBrowseFoodSafetyPrev,
  clampFoodSafetyBrowseDate,
  isFoodSafetyLocalDate,
  shiftFoodSafetyLocalDate,
} from 'src/utils/foodSafetyHome'

/**
 * Compact ◀ date ▶ control for Food Safety day browse.
 * Uses antd3 Icon (not @ant-design/icons) so arrows are visible on Buttons.
 * Free venues: previous at the 14-day edge stays clickable and calls onBrowseLocked
 * so we can show the Starter history paywall (honest block, not a silent dead button).
 */
const FoodSafetyDayNav = ({
  localDate,
  historyAccess,
  browseWindow: browseWindowProp,
  onChangeDate,
  onBrowseLocked,
  disabled = false,
  'data-testid': testId = 'food-safety-day-nav',
}) => {
  const browseWindow =
    browseWindowProp || buildFoodSafetyBrowseWindow(historyAccess, localDate)

  const signalLockedBrowse = requestedDate => {
    if (browseWindow.fullHistoryUnlocked || !onBrowseLocked) return false
    if (
      isFoodSafetyLocalDate(requestedDate) &&
      browseWindow.earliestLocalDate &&
      requestedDate < browseWindow.earliestLocalDate
    ) {
      onBrowseLocked()
      return true
    }
    return false
  }

  const goToDate = nextDate => {
    if (disabled || !onChangeDate) return
    if (signalLockedBrowse(nextDate)) return
    const clamped = clampFoodSafetyBrowseDate(nextDate, browseWindow)
    if (!clamped || clamped === localDate) return
    onChangeDate(clamped)
  }

  const canPrev = Boolean(
    localDate && canBrowseFoodSafetyPrev(localDate, browseWindow),
  )
  const canNext = Boolean(
    localDate && canBrowseFoodSafetyNext(localDate, browseWindow),
  )
  const prevOpensPaywall =
    !canPrev && !browseWindow.fullHistoryUnlocked && Boolean(onBrowseLocked)

  const onPrev = () => {
    if (disabled || !localDate) return
    if (!canPrev) {
      if (prevOpensPaywall) onBrowseLocked()
      return
    }
    goToDate(shiftFoodSafetyLocalDate(localDate, -1))
  }

  const onNext = () => {
    if (disabled || !localDate || !canNext) return
    goToDate(shiftFoodSafetyLocalDate(localDate, 1))
  }

  return (
    <DateNav data-testid={testId}>
      <Button
        onClick={onPrev}
        disabled={disabled || !localDate || (!canPrev && !prevOpensPaywall)}
        aria-label={
          prevOpensPaywall ? 'Earlier days unlock on Starter' : 'Previous day'
        }
        data-testid='food-safety-day-prev'
        data-paywall={prevOpensPaywall ? 'true' : 'false'}
      >
        <Icon type='left' />
      </Button>
      <DatePicker
        value={localDate ? moment(localDate, 'YYYY-MM-DD') : null}
        onChange={value => {
          if (!value) return
          goToDate(value.format('YYYY-MM-DD'))
        }}
        allowClear={false}
        format='ddd D MMM'
        disabled={disabled || !localDate}
        style={{ width: '9.75rem' }}
        disabledDate={current => {
          if (!current) return false
          const iso = current.format('YYYY-MM-DD')
          // Keep Free earlier dates selectable so picking one can open the paywall
          // via goToDate → signalLockedBrowse. Still block future / after latest.
          if (
            !browseWindow.fullHistoryUnlocked &&
            browseWindow.earliestLocalDate &&
            iso < browseWindow.earliestLocalDate
          ) {
            return false
          }
          const clamped = clampFoodSafetyBrowseDate(iso, browseWindow)
          return clamped !== iso
        }}
        aria-label='Day to view'
        data-testid='food-safety-day-picker'
      />
      <Button
        onClick={onNext}
        disabled={disabled || !localDate || !canNext}
        aria-label='Next day'
        data-testid='food-safety-day-next'
      >
        <Icon type='right' />
      </Button>
    </DateNav>
  )
}

const DateNav = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  flex-shrink: 0;
  max-width: 100%;

  .ant-calendar-picker {
    max-width: 100%;
  }
`

export default FoodSafetyDayNav
